/**
 * Review Section Autoslide Controller
 */
if (!customElements.get('review-slider')) {
  class ReviewSlider extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.autoTimer = null;
      this.isHovered = false;
      this.touchStartX = 0;
      this.touchEndX = 0;
    }

    connectedCallback() {
      this.track = this.querySelector('.m-review-slider__track');
      this.slides = Array.from(this.querySelectorAll('.m-review-card'));
      this.prevBtn = this.querySelector('.m-review-slider__btn--prev');
      this.nextBtn = this.querySelector('.m-review-slider__btn--next');
      this.dotsContainer = this.querySelector('.m-review-slider__dots');

      if (!this.track || this.slides.length === 0) return;

      this.autoplaySpeed = parseInt(this.dataset.speed || '4000', 10);
      this.isAutoplay = this.dataset.autoplay === 'true';

      this.init();
    }

    init() {
      this.updateVisibleSlides();
      this.createDots();
      this.bindEvents();
      this.goToSlide(0);

      if (this.isAutoplay) {
        this.startAutoplay();
      }
    }

    getVisibleCount() {
      const width = window.innerWidth;
      const desktopCols = parseInt(this.dataset.columnsDesktop || '3', 10);
      const tabletCols = parseInt(this.dataset.columnsTablet || '2', 10);
      const mobileCols = parseInt(this.dataset.columnsMobile || '1', 10);

      if (width >= 1024) return desktopCols;
      if (width >= 640) return tabletCols;
      return mobileCols;
    }

    updateVisibleSlides() {
      this.visibleCount = this.getVisibleCount();
      this.maxIndex = Math.max(0, this.slides.length - this.visibleCount);
    }

    createDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';
      
      const totalPages = this.maxIndex + 1;
      if (totalPages <= 1) {
        this.dotsContainer.style.display = 'none';
        return;
      }
      this.dotsContainer.style.display = 'flex';

      for (let i = 0; i <= this.maxIndex; i++) {
        const dot = document.createElement('button');
        dot.className = `m-review-slider__dot${i === 0 ? ' is-active' : ''}`;
        dot.setAttribute('aria-label', `Go to review slide ${i + 1}`);
        dot.addEventListener('click', () => {
          this.goToSlide(i);
          this.resetAutoplay();
        });
        this.dotsContainer.appendChild(dot);
      }
    }

    bindEvents() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.prev();
          this.resetAutoplay();
        });
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.next();
          this.resetAutoplay();
        });
      }

      // Pause on hover
      this.addEventListener('mouseenter', () => {
        this.isHovered = true;
        this.stopAutoplay();
      });

      this.addEventListener('mouseleave', () => {
        this.isHovered = false;
        if (this.isAutoplay) this.startAutoplay();
      });

      // Touch swipe support
      this.addEventListener('touchstart', (e) => {
        this.touchStartX = e.touches[0].clientX;
        this.stopAutoplay();
      }, { passive: true });

      this.addEventListener('touchend', (e) => {
        this.touchEndX = e.changedTouches[0].clientX;
        this.handleSwipe();
        if (this.isAutoplay && !this.isHovered) this.startAutoplay();
      }, { passive: true });

      // Window resize listener
      window.addEventListener('resize', () => {
        const prevMax = this.maxIndex;
        this.updateVisibleSlides();
        if (prevMax !== this.maxIndex) {
          this.createDots();
          if (this.currentIndex > this.maxIndex) {
            this.currentIndex = this.maxIndex;
          }
          this.goToSlide(this.currentIndex);
        }
      });
    }

    handleSwipe() {
      const diff = this.touchStartX - this.touchEndX;
      if (Math.abs(diff) > 40) {
        if (diff > 0) {
          this.next();
        } else {
          this.prev();
        }
      }
    }

    goToSlide(index) {
      if (this.maxIndex === 0) {
        this.currentIndex = 0;
      } else {
        this.currentIndex = Math.max(0, Math.min(index, this.maxIndex));
      }

      const percent = (100 / this.visibleCount) * this.currentIndex;
      this.track.style.transform = `translateX(-${percent}%)`;

      // Update dots
      if (this.dotsContainer) {
        const dots = Array.from(this.dotsContainer.querySelectorAll('.m-review-slider__dot'));
        dots.forEach((dot, idx) => {
          dot.classList.toggle('is-active', idx === this.currentIndex);
        });
      }
    }

    next() {
      if (this.currentIndex >= this.maxIndex) {
        this.goToSlide(0);
      } else {
        this.goToSlide(this.currentIndex + 1);
      }
    }

    prev() {
      if (this.currentIndex <= 0) {
        this.goToSlide(this.maxIndex);
      } else {
        this.goToSlide(this.currentIndex - 1);
      }
    }

    startAutoplay() {
      this.stopAutoplay();
      this.autoTimer = setInterval(() => {
        if (!this.isHovered) {
          this.next();
        }
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.autoTimer) {
        clearInterval(this.autoTimer);
        this.autoTimer = null;
      }
    }

    resetAutoplay() {
      if (this.isAutoplay) {
        this.startAutoplay();
      }
    }

    disconnectedCallback() {
      this.stopAutoplay();
    }
  }

  customElements.define('review-slider', ReviewSlider);
}
